import React, { useState, useEffect } from "react";
import { clientes, downloadExcel, exportData } from "../../lib/api";
import { DataTable } from "../shared/DataTable";
import { StatusBadge } from "../shared/StatusBadge";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "../ui/dialog";
import { Plus, Search, Download, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../../contexts/AuthContext";
import { formatDate } from "../../lib/utils";

export function ClientesPage() {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const { isAdmin } = useAuth();

  const [formData, setFormData] = useState({
    razao_social: "",
    nome_fantasia: "",
    cnpj: "",
    email: "",
    telefone: "",
    cidade: "",
    estado: "",
    segmento: "",
  });

  const fetchData = async () => {
    try {
      const response = await clientes.list();
      setData(response.data);
    } catch (error) {
      toast.error("Erro ao carregar clientes");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingItem) {
        await clientes.update(editingItem.id, formData);
        toast.success("Cliente atualizado com sucesso!");
      } else {
        await clientes.create(formData);
        toast.success("Cliente criado com sucesso!");
      }
      setIsDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao salvar cliente");
    }
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      razao_social: item.razao_social || "",
      nome_fantasia: item.nome_fantasia || "",
      cnpj: item.cnpj || "",
      email: item.email || "",
      telefone: item.telefone || "",
      cidade: item.cidade || "",
      estado: item.estado || "",
      segmento: item.segmento || "",
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Deseja realmente excluir este cliente?")) return;
    try {
      await clientes.delete(id);
      toast.success("Cliente excluído com sucesso!");
      fetchData();
    } catch (error) {
      toast.error("Erro ao excluir cliente");
    }
  };

  const resetForm = () => {
    setEditingItem(null);
    setFormData({
      razao_social: "",
      nome_fantasia: "",
      cnpj: "",
      email: "",
      telefone: "",
      cidade: "",
      estado: "",
      segmento: "",
    });
  };

  const handleExport = async () => {
    try {
      await downloadExcel(exportData.clientes, "clientes.xlsx");
      toast.success("Relatório exportado!");
    } catch (error) {
      toast.error("Erro ao exportar");
    }
  };

  const filteredData = data.filter(
    (item) =>
      item.razao_social?.toLowerCase().includes(search.toLowerCase()) ||
      item.nome_fantasia?.toLowerCase().includes(search.toLowerCase()) ||
      item.cnpj?.includes(search)
  );

  const columns = [
    { key: "razao_social", header: "Razão Social" },
    { key: "nome_fantasia", header: "Nome Fantasia" },
    { key: "cnpj", header: "CNPJ" },
    { key: "cidade", header: "Cidade" },
    { key: "estado", header: "UF", width: "60px" },
    {
      key: "status",
      header: "Status",
      render: (value) => <StatusBadge status={value} />,
    },
    {
      key: "data_cadastro",
      header: "Cadastro",
      render: (value) => formatDate(value),
    },
    {
      key: "actions",
      header: "",
      width: "100px",
      render: (_, row) =>
        isAdmin() && (
          <div className="flex gap-1">
            <Button
              variant="ghost"
              size="icon"
              onClick={(e) => {
                e.stopPropagation();
                handleEdit(row);
              }}
              data-testid={`edit-cliente-${row.id}`}
            >
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={(e) => {
                e.stopPropagation();
                handleDelete(row.id);
              }}
              data-testid={`delete-cliente-${row.id}`}
            >
              <Trash2 className="h-4 w-4 text-destructive" />
            </Button>
          </div>
        ),
    },
  ];

  return (
    <div className="space-y-6" data-testid="clientes-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold">Clientes</h1>
          <p className="text-muted-foreground">Gerencie sua base de clientes</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExport} data-testid="export-clientes-btn">
            <Download className="h-4 w-4 mr-2" />
            Exportar
          </Button>
          {isAdmin() && (
            <Dialog open={isDialogOpen} onOpenChange={(open) => {
              setIsDialogOpen(open);
              if (!open) resetForm();
            }}>
              <DialogTrigger asChild>
                <Button data-testid="add-cliente-btn">
                  <Plus className="h-4 w-4 mr-2" />
                  Novo Cliente
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>{editingItem ? "Editar Cliente" : "Novo Cliente"}</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>Razão Social *</Label>
                      <Input
                        value={formData.razao_social}
                        onChange={(e) => setFormData({ ...formData, razao_social: e.target.value })}
                        required
                        data-testid="cliente-razao-social"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Nome Fantasia</Label>
                      <Input
                        value={formData.nome_fantasia}
                        onChange={(e) => setFormData({ ...formData, nome_fantasia: e.target.value })}
                        data-testid="cliente-nome-fantasia"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>CNPJ *</Label>
                      <Input
                        value={formData.cnpj}
                        onChange={(e) => setFormData({ ...formData, cnpj: e.target.value })}
                        required
                        data-testid="cliente-cnpj"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Email</Label>
                      <Input
                        type="email"
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        data-testid="cliente-email"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Telefone</Label>
                      <Input
                        value={formData.telefone}
                        onChange={(e) => setFormData({ ...formData, telefone: e.target.value })}
                        data-testid="cliente-telefone"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Segmento</Label>
                      <Input
                        value={formData.segmento}
                        onChange={(e) => setFormData({ ...formData, segmento: e.target.value })}
                        data-testid="cliente-segmento"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Cidade</Label>
                      <Input
                        value={formData.cidade}
                        onChange={(e) => setFormData({ ...formData, cidade: e.target.value })}
                        data-testid="cliente-cidade"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label>Estado</Label>
                      <Input
                        value={formData.estado}
                        onChange={(e) => setFormData({ ...formData, estado: e.target.value })}
                        maxLength={2}
                        data-testid="cliente-estado"
                      />
                    </div>
                  </div>
                  <div className="flex justify-end gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>
                      Cancelar
                    </Button>
                    <Button type="submit" data-testid="save-cliente-btn">
                      {editingItem ? "Salvar" : "Criar"}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>
      </div>

      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Buscar por nome ou CNPJ..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="search-clientes"
          />
        </div>
      </div>

      <DataTable
        columns={columns}
        data={filteredData}
        loading={loading}
        emptyMessage="Nenhum cliente encontrado"
      />
    </div>
  );
}
